import { selectProjectUsers } from "@/store/project-selector";
import { useAppSelector } from "@/store/store-hooks";
import {
  Autocomplete,
  AutoCompleteOption,
  AutoCompleteSelectedOption,
  FaroText,
  TeamIcon,
} from "@faro-lotv/flat-ui";
import { mayBeEmail } from "@faro-lotv/foundation";
import { GenericUserInfo } from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { isEqual } from "es-toolkit";
import { useEffect, useMemo, useState, type JSX } from "react";

type UsersInviteProps = {
  onEmailsChanged(emails: string[]): void;
};

/** @returns A combobox to invite users to the snapshot */
export function UsersInvite({
  onEmailsChanged,
}: UsersInviteProps): JSX.Element {
  const [selectedOptions, setSelectedOptions] = useState<
    AutoCompleteSelectedOption[]
  >([]);

  const [text, setText] = useState("");

  useEffect(() => {
    const emails = selectedOptions.map((option) =>
      typeof option === "string" ? option : option.value,
    );
    // A valid email still typed in the input counts as entered,
    // so the user is not forced to confirm it with Enter
    if (mayBeEmail(text) && !emails.includes(text)) {
      emails.push(text);
    }
    onEmailsChanged(emails);
  }, [selectedOptions, text, onEmailsChanged]);

  const projectUsers = useAppSelector(selectProjectUsers, isEqual);
  const options = useMemo(() => {
    const [teams, users] = projectUsers.reduce<
      [GenericUserInfo[], GenericUserInfo[]]
    >(
      (acc, user) => {
        if (user.authorityType === "UserGroupRef") {
          acc[0].push(user);
        } else {
          acc[1].push(user);
        }
        return acc;
      },
      [[], []],
    );
    return [
      ...teams.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
      ...users.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    ].map(toAutoCompleteOption);
  }, [projectUsers]);

  return (
    <Stack sx={{ gap: 1 }}>
      <FaroText variant="heading12">Invite users</FaroText>
      <Autocomplete
        options={options}
        selectedOptions={selectedOptions}
        handleChange={setSelectedOptions}
        onInputChange={setText}
        isValid={mayBeEmail}
        placeHolder="Email(s), comma separated"
      />
    </Stack>
  );
}

function toAutoCompleteOption(user: GenericUserInfo): AutoCompleteOption {
  const isGroup = user.authorityType === "UserGroupRef";
  return {
    title: user.name ?? user.email,
    subtitle: isGroup ? "" : user.email,
    avatarImage: user.profileImageUrl,
    avatarIcon: isGroup ? <TeamIcon sx={{ width: "16px" }} /> : undefined,
    value: user.email,
  };
}
